/**
 * career-workshop-list — the Workshops area (route `/workshops`). Three workshop
 * cards (W1 Selbstbild → W2 Suchprofil → W3 Profiler) with honest gate state
 * (locked / available / in-progress / completed), the blocking predecessor named
 * when locked (AX-10 gated ≠ broken), a meaningful step count (AX-6), and an entry
 * affordance (start / resume / revisit). Fetch-driven off `workshops.list`; the
 * workshop push streams reconcile live status flips without a reload.
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, PushToken, RouterToken, SessionToken, WorkshopStoreToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { careerStream, type ShellSession, type WorkshopStore } from "../domain"

type Gate = "locked" | "available" | "in_progress" | "completed"

type W1StageKey = "cardgame" | "interview" | "sektion"
type W1StageStatus = "done" | "current" | "todo"

interface W1Stage {
  key: W1StageKey
  status: W1StageStatus
  href: string
  round: number | null
  roundTotal: number | null
}

interface WorkshopSummaryView {
  type: string
  slug: string
  name: string
  purpose: string
  gate: Gate
  blockedBy: { slug: string; name: string } | null
  stepCount: number
  answeredCount: number
  hasContext: boolean
  /** W1 ONLY: the three-stage »Deine Werte« track (absent on W2/W3). */
  stages?: W1Stage[]
}

const STAGE_LABEL: Record<W1StageKey, CareerKey> = {
  cardgame: "workshop.w1.stage.cardgame",
  interview: "workshop.w1.stage.interview",
  sektion: "workshop.w1.stage.sektion",
}
const STAGE_MARK: Record<W1StageStatus, string> = { done: "✓", current: "◉", todo: "○" }

const GATE_KEY: Record<Gate, CareerKey> = {
  locked: "workshop.gate.locked",
  available: "workshop.gate.available",
  in_progress: "workshop.gate.inProgress",
  completed: "workshop.gate.completed",
}
const GATE_CLASS: Record<Gate, string> = {
  locked: "is-locked",
  available: "is-available",
  in_progress: "is-in-progress",
  completed: "is-completed",
}

const WORKSHOP_TYPES = ["self_knowledge", "search_profile", "profiler"] as const

export class WorkshopListVm implements ViewModel {
  readonly workshops: Signal<WorkshopSummaryView[]> = signal<WorkshopSummaryView[]>([])
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly starting: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(
    private readonly router: Router,
    private readonly push: PushChannel,
    private readonly store: WorkshopStore,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.renderDeps = [this.workshops, this.error, this.loaded, this.starting]
  }

  attach(): () => void {
    void this.refresh()
    const userId = this.session.user?.sub
    let stop = () => {}
    if (userId) {
      this.push.subscribe(WORKSHOP_TYPES.map((t) => careerStream(userId, "Workshop", `urn:career:workshop:${userId}:${t}`)))
      // Live status flips (completion in another tab) → re-fetch the rich summary.
      stop = effect([this.store.list("alle")], () => void this.refresh())
    }
    return stop
  }

  private async refresh(): Promise<void> {
    try {
      this.workshops.set(await apiGet<WorkshopSummaryView[]>("/api/workshops"))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  async start(slug: string): Promise<void> {
    this.starting.set(slug)
    try {
      await apiPost(`/api/workshops/${slug}/start`)
      void this.router.navigate(`/workshops/${slug}`)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.starting.set(null)
    }
  }

  open(slug: string): void {
    void this.router.navigate(`/workshops/${slug}`)
  }

  /** Navigate into a W1 stage (resume / revisit) by its English route. */
  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerWorkshopList extends KitElement<WorkshopListVm> {
  protected createViewModel(ctx: KitContext): WorkshopListVm {
    return new WorkshopListVm(
      ctx.inject(RouterToken),
      ctx.inject(PushToken),
      ctx.inject(WorkshopStoreToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  /** The honest one-line state for a stage, composed UI-side from its structured fields. */
  private stageDetail(vm: WorkshopListVm, stage: W1Stage): string {
    if (stage.status === "done") {
      if (stage.key === "cardgame") return vm.t("workshop.w1.detail.pyramidDone")
      if (stage.key === "interview") return vm.t("workshop.w1.detail.interviewDone")
      return vm.t("workshop.w1.detail.sektionDone")
    }
    if (stage.key === "interview" && stage.round !== null && stage.roundTotal !== null) {
      return vm.t("workshop.w1.detail.round", { round: stage.round, total: stage.roundTotal })
    }
    return vm.t("workshop.w1.detail.open")
  }

  private stageRow(vm: WorkshopListVm, stage: W1Stage): TemplateResult {
    const action =
      stage.status === "current"
        ? html`<button class="w1-stage-cta" data-act="go" data-url=${stage.href} lang="de">${vm.t("workshop.w1.resume")}</button>`
        : stage.status === "done"
          ? html`<button class="w1-stage-revisit ghost" data-act="go" data-url=${stage.href} lang="de">${vm.t("workshop.w1.revisit")}</button>`
          : nothing
    return html`
      <li class=${classMap({ "w1-stage": true, ["is-" + stage.status]: true })}>
        <span class="w1-stage-mark" aria-hidden="true">${STAGE_MARK[stage.status]}</span>
        <span class="w1-stage-label" lang="de">${vm.t(STAGE_LABEL[stage.key])}</span>
        <span class="w1-stage-detail" lang="de">${this.stageDetail(vm, stage)}</span>
        <span class="w1-stage-action">${action}</span>
      </li>
    `
  }

  /** W1 »Deine Werte« — a 3-stage card resolved from real track state (AX-6/AX-10). */
  private stageCard(vm: WorkshopListVm, w: WorkshopSummaryView, stages: W1Stage[]): TemplateResult {
    const urn = `urn:career:workshop:${w.slug}` as Urn
    const doneCount = stages.filter((s) => s.status === "done").length
    return html`
      <li urn=${urn} class=${classMap({ "workshop-card": true, "w1-card": true, "surface-panel": true, [GATE_CLASS[w.gate]]: true })}>
        <div class="workshop-card-head">
          <h2 class="workshop-name" lang="de">${w.name}</h2>
          <span class="workshop-progress-chip chip ${GATE_CLASS[w.gate]}" lang="de">${vm.t("workshop.progress.stages", { done: doneCount, total: stages.length })}</span>
        </div>
        <p class="workshop-purpose" lang="de">${w.purpose}</p>
        <ol class="w1-stages" role="list">${stages.map((s) => this.stageRow(vm, s))}</ol>
      </li>
    `
  }

  private card(vm: WorkshopListVm, w: WorkshopSummaryView): TemplateResult {
    if (w.type === "self_knowledge" && w.stages && w.stages.length > 0) return this.stageCard(vm, w, w.stages)
    const urn = `urn:career:workshop:${w.slug}` as Urn
    const busy = vm.starting.value === w.slug
    const action =
      w.gate === "locked"
        ? nothing
        : w.gate === "completed"
          ? html`<button data-act="start-workshop" data-slug=${w.slug} lang="de">${vm.t("workshop.action.revisit")}</button>`
          : w.gate === "in_progress"
            ? html`<button data-act="start-workshop" data-slug=${w.slug} class=${classMap({ "is-pending": busy })} ?disabled=${busy} lang="de">${vm.t("workshop.action.resume")}</button>`
            : html`<button data-act="start-workshop" data-slug=${w.slug} class=${classMap({ "is-pending": busy })} ?disabled=${busy} lang="de">${vm.t("workshop.action.start")}</button>`
    return html`
      <li urn=${urn} class=${classMap({ "workshop-card": true, "surface-panel": true, [GATE_CLASS[w.gate]]: true })}>
        <div class="workshop-card-head">
          <h2 class="workshop-name" lang="de">${w.name}</h2>
          <span class="workshop-gate chip ${GATE_CLASS[w.gate]}" lang="de">${vm.t(GATE_KEY[w.gate])}</span>
        </div>
        <p class="workshop-purpose" lang="de">${w.purpose}</p>
        <p class="workshop-progress">
          <span class="chip" lang="de">${vm.t("workshop.progress", { answered: w.answeredCount, total: w.stepCount })}</span>
          ${w.gate === "locked" && w.blockedBy
            ? html`<span class="workshop-blocked chip is-locked" lang="de">${vm.t("workshop.lockedBy", { name: w.blockedBy.name })}</span>`
            : nothing}
        </p>
        <div class="workshop-actions">${action}</div>
      </li>
    `
  }

  protected render(vm: WorkshopListVm): TemplateResult {
    const err = vm.error.value
    return html`
      <section class="surface workshops" aria-labelledby="workshops-title">
        <h1 id="workshops-title" class="surface-title" lang="de">${vm.t("surface.workshops.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("workshops.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${vm.workshops.value.length === 0 && vm.loaded.value
          ? html`<p class="surface-empty" lang="de">${vm.t("workshops.empty")}</p>`
          : html`<ol class="workshop-list" role="list">
              ${repeat(vm.workshops.value, (w) => w.slug, (w) => this.card(vm, w))}
            </ol>`}
      </section>
    `
  }

  protected override actions(vm: WorkshopListVm): ActionMap {
    return {
      "start-workshop": ({ slug }) => {
        if (slug) void vm.start(slug)
      },
      go: ({ url }) => {
        if (url) vm.go(url)
      },
    }
  }
}
